import { Box } from '@mui/material';
import {
  CalendarView,
  ListView,
  MerchantCustomers,
  CalendarHeader,
  CalendarHeaderVariantType,
} from './components';
import { useMemo } from 'react';
import { Experience, Listing } from '@/types';
import { DateClickArg } from '@fullcalendar/interaction';
import { formatListing } from '@/utils/schedulingUtils';
import { EVENTS_FILTERS, VIEWS } from '@/constants';

export type CalendarFiltersType = {
  eventsType?: string;
  productsIds?: string[];
};

type CalendarProps = {
  filters?: CalendarFiltersType;
  onFiltersChange: (filters: CalendarFiltersType) => void;

  selectedDate: Date;
  onDateChange: (date: Date) => void;

  onCalendarClick: (arg: DateClickArg) => void;
  onEventClick: (id: string) => void;
  isLoading?: boolean;
  events: Listing[];
  products: Experience[];
  selectedView: string;
  setSelectedView: (view: string) => void;
  headerVariant?: CalendarHeaderVariantType;
  id?: string;
};

export const Calendar = ({
  filters,
  onFiltersChange,
  selectedDate,
  onDateChange,
  onCalendarClick,
  onEventClick,
  isLoading,
  events,
  products,
  selectedView,
  setSelectedView,
  headerVariant,
  id,
}: CalendarProps) => {
  const formattedEvents = useMemo(
    () => formatListing(events || [], products || []),
    [events, products]
  );

  const renderComponentContent = () => {
    if (filters?.eventsType === EVENTS_FILTERS.CUSTOMERS) {
      return (
        <MerchantCustomers
          selectedProductIds={filters?.productsIds}
          selectedDate={selectedDate}
          onChangeSelectedDate={onDateChange}
        />
      );
    }
    if (selectedView === VIEWS.LIST) {
      return (
        <ListView
          selectedDate={selectedDate}
          selectedProductIds={filters?.productsIds || []}
          eventsFilter={filters?.eventsType || ''}
          products={products}
          onEventClick={onEventClick}
        />
      );
    }
    if (
      selectedView === VIEWS.MONTH ||
      selectedView === VIEWS.WEEK ||
      selectedView === VIEWS.DAY
    ) {
      return (
        <CalendarView
          isLoading={isLoading}
          events={formattedEvents}
          height="auto"
          width="100%"
          handleNewEventClick={onCalendarClick}
          handleEventClick={onEventClick}
          selectedView={selectedView}
          selectedDate={selectedDate}
        />
      );
    }
    return null;
  };

  return (
    <Box>
      <CalendarHeader
        selectedDate={selectedDate}
        onChangeSelectedDate={onDateChange}
        onChangeSelectedProducts={productsIds =>
          onFiltersChange({ ...filters, productsIds })
        }
        onChangeSelectedView={setSelectedView}
        selectedView={selectedView}
        onChangeSelectedFilter={eventsType =>
          onFiltersChange({ ...filters, eventsType })
        }
        selectedFilter={filters?.eventsType}
        products={products.map(product => ({
          title: product.title,
          value: product.id,
        }))}
        variant={headerVariant}
        id={id}
      />
      {renderComponentContent()}
    </Box>
  );
};
